import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { hasDisplayPrice } from '../../../utils/priceDisplay';

const ProductInfo = ({ product, onRequestQuote }) => {
  const [quantity, setQuantity] = useState(1);

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 99) {
      setQuantity(newQuantity);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <div className="space-y-6">
      {/* Product Header */}
      <div>
        {/* Category and badges removed per user request */}
        <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
          {product?.name}
        </h1>
        <p className="text-text-secondary mb-4">
          {product?.shortDescription || product?.description}
        </p>
        {/* 
        <div className="flex items-center gap-4">
          <span className="text-sm text-text-secondary">SKU: {product?.sku}</span>
          <div className="flex items-center gap-1">
            <Icon name="Building" size={16} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">{product?.manufacturer}</span>
          </div>
        </div>
        */}
      </div>
      {/* Certifications section removed per user request */}
      {/* Pricing — omit block when no positive list price (no RM 0 for unset amounts) */}
      {/* Quick Specs */}
      <div className="bg-muted rounded-lg p-4">
        <h3 className="font-semibold text-text-primary mb-3 flex items-center">
          <Icon name="Info" size={18} className="mr-2" />
          Quick Specifications
        </h3>
        <div className="space-y-2">
          {(product?.quickSpecs || [
            { label: 'Model', value: product?.manufacturer || 'Endosurgery Malaysia' },
            { label: 'Sterilization', value: 'Gamma / ETO' },
            { label: 'Material', value: 'Medical Grade' },
            { label: 'Certification', value: 'ISO 13485, CE' }
          ])?.map((spec, index) => (
            <div key={index} className="flex justify-between items-center py-1.5 border-b border-border/10 last:border-0">
              <span className="text-sm text-text-secondary">{spec?.label}:</span>
              <span className="text-sm font-bold text-text-primary px-2">{spec?.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            {hasDisplayPrice(product?.price) && (
              <>
                <span className="text-sm text-text-secondary">Starting from</span>
                <div className="text-2xl font-bold text-text-primary">
                  {formatPrice(product.price)}
                </div>
              </>
            )}
            {hasDisplayPrice(product?.originalPrice ?? product?.original_price) && (
              <span className="text-sm text-text-secondary line-through">
                {formatPrice(product?.originalPrice ?? product?.original_price)}
              </span>
            )}
          </div>
          <div className="text-right">
            {(() => {
              const status = product?.availability;
              const isComingSoon = status === 'Coming Soon' || status === 'Pre-Order';
              const isInStock = status === 'In Stock';
              
              const baseClasses = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border animate-pulse-glow";
              const themeClasses = isInStock 
                ? "bg-success/10 text-success border-success/20" 
                : isComingSoon 
                  ? "bg-warning/10 text-warning border-warning/20" 
                  : "bg-error/10 text-error border-error/20";
              
              const iconName = isInStock ? "CheckCircle" : isComingSoon ? "Clock" : "AlertTriangle";
              
              return (
                <div className={`${baseClasses} ${themeClasses}`}>
                  <Icon name={iconName} size={12} className="mr-1.5" />
                  <span className="uppercase tracking-wider">{status}</span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Quantity Selector commented out per user request */}
        {/*
        <div className="flex items-center gap-4 mb-4">
          <span className="text-sm font-medium text-text-primary">Quantity:</span>
          <div className="flex items-center border border-border rounded-lg">
            <button
              onClick={() => handleQuantityChange(-1)}
              disabled={quantity <= 1}
              className="w-10 h-10 flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Minus" size={16} />
            </button>
            <span className="w-12 text-center font-medium">{quantity}</span>
            <button
              onClick={() => handleQuantityChange(1)}
              disabled={quantity >= 99}
              className="w-10 h-10 flex items-center justify-center hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Plus" size={16} />
            </button>
          </div>
        </div>
        */}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            variant="default"
            fullWidth
            iconName="MessageSquare"
            iconPosition="left"
            onClick={() => onRequestQuote(product, quantity)}
          >
            Request Quote
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              iconName="Phone" 
              iconPosition="left"
              onClick={() => window.open('tel:+60321100348', '_self')}
            >
              Call Now
            </Button>
            <Button 
              variant="outline" 
              iconName="Share2" 
              iconPosition="left"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: product?.name,
                    text: `Check out this medical device: ${product?.name}`,
                    url: window.location.href
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }
              }}
            >
              Share
            </Button>
          </div>
        </div>
      </div>
      {/* Contact Info */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <h3 className="font-semibold text-text-primary mb-2 flex items-center">
          <Icon name="Headphones" size={18} className="mr-2 text-primary" />
          Need Expert Advice?
        </h3>
        <p className="text-sm text-text-secondary mb-3">
          Our medical device specialists are here to help you make the right choice.
        </p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Icon name="Phone" size={14} className="text-primary" />
            <span className="font-medium">+60 3-2110 0348</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Mail" size={14} className="text-primary" />
            <span className="font-medium">info@endosurgery.com.my</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;